import React, { useEffect, useState } from "react";
import { ParallaxProvider, Parallax } from "react-scroll-parallax";
import aboutimg from "../../assets/aboutus.svg";
import Loader from "../loader";
import aboutData from "../../data/about.json";
import Helmet from "react-helmet";
import LazyOnScroll from "../../ui/LazyOnScroll";

interface TeamMember {
  name: string;
  role: string;
  bio: string;
  image: string;
}

interface PhilosophyPoint {
  description: string;
}

interface AboutData {
  introTitle: string;
  introText: string;
  philosophyTitle: string;
  philosophyDescription: string;
  philosophyPoints: PhilosophyPoint[];
  philosophyImage?: { asset: { url: string } } | null;
  teamTitle: string;
  teamIntroText: string;
  teamMembers: TeamMember[];
}

export default function AboutPage() {
  const [data, setData] = useState<AboutData | null>(null);
  const [showLoader, setShowLoader] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bgLoaded, setBgLoaded] = useState(false);
  const [bgImageUrl, setBgImageUrl] = useState<string>("");

  // Preload the background image as early as possible
  useEffect(() => {
    const preloadImage = () => {
      const img = new Image();
      img.src = aboutimg;

      // Set up the image URL immediately for faster rendering
      setBgImageUrl(aboutimg);

      img.onload = () => {
        setBgLoaded(true);
      };

      img.onerror = () => {
        console.error("Failed to load background image");
        setBgLoaded(true); // Still set to true to show the page
      };
    };

    // Start preloading immediately
    preloadImage();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShowLoader(true), 300);

    try {
      setData({
        ...aboutData,
        teamMembers: aboutData.teamMembers.map((member: any) => ({
          ...member,
          image: member.image?.asset?.url || "/placeholder.svg",
        })),
      });
    } catch (error) {
      console.error("Error setting about data:", error);
    } finally {
      clearTimeout(timer);
      setShowLoader(false);
      setLoading(false);
    }

    return () => clearTimeout(timer);
  }, []);

  if (loading && showLoader) {
    return <Loader />;
  }

  if (loading && !showLoader) {
    return <div style={{ minHeight: "100vh" }}></div>;
  }

  return (
    <ParallaxProvider>
      <div className="flex flex-col min-h-screen">
        <Helmet>
          <title>About Us – Walls</title>
          <meta
            name="description"
            content="Founded by two visionary partners, our firm is dedicated to transforming spaces into inspiring environments that reflect both functionality and aesthetic excellence."
          />
          <meta property="og:image" content="/logo.jpg" />
          {/* Preload the background image in the head */}
          <link rel="preload" href={aboutimg} as="image" />
        </Helmet>
        <main className="flex-1">
          {/* Hero Section */}
          <LazyOnScroll>
            <section
              className="relative h-[100vh] py-16 md:py-24 text-white overflow-hidden"
              aria-label="About Hero Section"
            >
              {/* Background with smooth fade-in - keeping your original parallax structure */}
              <Parallax
                speed={-20}
                shouldAlwaysCompleteAnimation={true}
                className={`absolute w-full h-full transition-opacity duration-700 ease-out ${
                  bgLoaded ? "opacity-100" : "opacity-0"
                }`}
                style={{ willChange: "transform" }}
              >
                <div
                  className="h-full bg-center bg-no-repeat bg-cover"
                  style={{
                    backgroundImage: `url(${bgImageUrl})`,
                    transform: "translateZ(0)", // Force hardware acceleration
                  }}
                />
              </Parallax>

              {/* Fallback background color while image loads */}
              {!bgLoaded && (
                <div
                  className="absolute inset-0 w-full h-full bg-gradient-to-br from-gray-100 to-gray-200"
                  style={{
                    background:
                      "linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)",
                  }}
                />
              )}

              {/* Content overlay */}
              <div className="absolute top-8 left-8 z-10 text-black py-4">
                <h2 className="text-3xl sm:text-4xl text-[#484331]">
                  About Walls
                </h2>
              </div>
            </section>
          </LazyOnScroll>

          {/* Philosophy Section */}
          <LazyOnScroll delay={80}>
            <section className="py-20 md:py-32 ">
              <div className="container max-w-screen-xl mx-auto px-4 md:px-8">
                <div className="max-w-4xl mx-auto">
                  <div className="flex justify-center mb-12">
                    <div
                      className="w-16 h-1 rounded-full"
                      style={{
                        background:
                          "linear-gradient(to right, #484331, #2f2c21)",
                      }}
                    />
                  </div>

                  <div className="space-y-12">
                    <div className="text-center">
                      <p className="text-lg md:text-2xl leading-relaxed  tracking-wide">
                        {data?.introText.slice(0, 256)}
                      </p>
                    </div>

                    <div className="flex justify-center">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 bg-[#484331] rounded-full opacity-60" />
                        <div className="w-2 h-2 bg-[#484331] rounded-full opacity-80" />
                        <div className="w-2 h-2 bg-[#484331] rounded-full opacity-60" />
                      </div>
                    </div>

                    <div className="text-center">
                      <p className="text-base md:text-2xl leading-relaxed  max-w-3xl mx-auto">
                        {data?.introText.slice(256, 714)}
                      </p>
                    </div>

                    <div className="flex justify-center">
                      <div className="w-24 h-px bg-gradient-to-r from-transparent via-[#484331] to-transparent" />
                    </div>

                    <div className="text-center">
                      <p className="text-base md:text-2xl leading-relaxed  max-w-2xl mx-auto">
                        {data?.introText.slice(714, 967)}
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-center mt-16">
                    <div className="w-8 h-8 border-2 border-[#484331] rounded-full flex items-center justify-center">
                      <div className="w-3 h-3 bg-[#484331] rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </LazyOnScroll>

          {/* Team Section */}
          <LazyOnScroll delay={140}>
            <section className="py-16 md:py-24 bg-muted/50">
              <div className="container max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-12 xl:px-16">
                <div className="flex flex-col items-center text-center mb-12">
                  <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">
                    {data?.teamTitle}
                  </h2>
                  <p className="mt-4 max-w-[700px] text-muted-foreground md:text-xl">
                    {data?.teamIntroText}
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-8">
                  {data?.teamMembers.map((member, idx) => (
                    <div
                      key={idx}
                      className="flex flex-col items-center text-center"
                    >
                      <div className="relative h-64 w-64 overflow-hidden rounded-full mb-4">
                        <img
                          src={member.image}
                          alt={member.name}
                          className="object-cover w-full h-full rounded-full"
                          loading="lazy"
                        />
                      </div>
                      <h3 className="text-xl font-bold">{member.name}</h3>
                      <p className="text-primary font-medium text-[#484331]">
                        {member.role}
                      </p>
                      <p className="mt-2 text-muted-foreground">{member.bio}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </LazyOnScroll>
        </main>
      </div>
    </ParallaxProvider>
  );
}
